import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useListCommon, useMutationCommon } from "hooks/common/useCRUD";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import priorityScheduleService from "services/priorityScheduleService";

export const usePriorityScheduleList = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["prioritySchedule", "common"]);

  return useQuery(
    ["priority-schedule-list", payload?.params],
    () => priorityScheduleService.getPrioritySchedules(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("prioritySchedule"),
                status: t("common:message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: {} }),
      select: response => ({
        items: response?.items || [],
        meta: response?._meta,
      }),
    }
  );
};

export const useActivatePrioritySchedule = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["prioritySchedule"]);
  return useMutation(priorityScheduleService.activatePrioritySchedule, {
    onSuccess: response => {
      const { data } = response;
      addToast(
        t("actions.active", {
          status: t("actions.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError,
  });
};

export const useDeactivatePrioritySchedule = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["prioritySchedule"]);
  return useMutation(priorityScheduleService.deactivatePrioritySchedule, {
    onSuccess: response => {
      const { data } = response;
      addToast(
        t("actions.inactive", {
          status: t("actions.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError,
  });
};

export const useCreatePrioritySchedule = ({ onSuccess, onError }) => {
  const { mutation } = useMutationCommon({
    onSuccess,
    onError,
    mutationFn: priorityScheduleService.createPrioritySchedule,
    target: "prioritySchedule",
    translation: ["prioritySchedule"],
    translationAction: "create",
    isNotifyError: true,
    isNotifySuccess: true,
  });
  return mutation;
};

export const useUpdatePrioritySchedule = ({ onSuccess, onError }) => {
  const { mutation } = useMutationCommon({
    onSuccess,
    onError,
    mutationFn: priorityScheduleService.updatePrioritySchedule,
    target: "prioritySchedule",
    translation: ["prioritySchedule"],
    translationAction: "update",
    isNotifyError: true,
    isNotifySuccess: true,
  });
  return mutation;
};

export const useListRouteRunner = ({ payload, onSuccess, onError }) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "route",
    queryKey: ["routes-runner", payload?.key],
    queryFn: () => priorityScheduleService.listRouteRunner(payload),
  });

  return query;
};

export const useListRouteLocalitiesRunner = ({
  payload,
  onSuccess,
  onError,
}) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "routeLocalities",
    queryKey: ["routes-localities-runner", payload?.key],
    queryFn: () => priorityScheduleService.listRouteLocalitiesRunner(payload),
  });
  return query;
};

export const useListTimeSlotRunner = ({ payload, onSuccess, onError }) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "timeSlot",
    queryKey: ["time-slots-runner", payload?.key],
    queryFn: () => priorityScheduleService.listTimeSlotRunner(payload),
  });

  return query;
};

export const useListRunnerHubPrioritySchedule = ({
  payload,
  onSuccess,
  onError,
}) => {
  const { query } = useListCommon({
    isHavePagination: false,
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "runnerHub",
    queryKey: ["runner-hubs", payload?.key],
    queryFn: () => priorityScheduleService.listRunnerHub(payload),
  });

  return query;
};

export const useListRouteCollector = ({ payload, onSuccess, onError }) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "routeCollector",
    queryKey: ["routes-collector", payload?.key],
    queryFn: () => priorityScheduleService.listRouteCollector(payload),
  });

  return query;
};

export const useListRouteLocalitiesCollector = ({
  payload,
  onSuccess,
  onError,
}) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "routeLocalitiesCollector",
    queryKey: ["routes-localities-collector", payload?.key],
    queryFn: () =>
      priorityScheduleService.listRouteLocalitiesCollector(payload),
  });
  return query;
};

export const useListTimeSlotCollector = ({ payload, onSuccess, onError }) => {
  const { query } = useListCommon({
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "timeSlotCollector",
    queryKey: ["time-slots-collector", payload?.key],
    queryFn: () => priorityScheduleService.listTimeSlotCollector(payload),
  });

  return query;
};

export const useListCollectorHubPrioritySchedule = ({
  payload,
  onSuccess,
  onError,
}) => {
  const { query } = useListCommon({
    isHavePagination: false,
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "collectorHub",
    queryKey: ["collector-hubs", payload?.key],
    queryFn: () => priorityScheduleService.listCollectorHub(payload),
  });

  return query;
};

export const useMyHubsPriorityScheduleList = ({
  payload,
  onSuccess,
  onError,
}) => {
  const { query } = useListCommon({
    isHavePagination: false,
    customFieldHaveItems: "data.childs_hub",
    payload,
    onSuccess,
    onError,
    translation: ["prioritySchedule"],
    target: "myHubs",
    queryKey: ["my-hubs", payload?.key],
    queryFn: () => priorityScheduleService.listMyHubs(payload),
  });

  return query;
};
